/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import {
  delay,
  call,
  select,
  put,
  all,
  race,
  take,
  putResolve,
} from "typed-redux-saga";
import {
  selectIsRollingMode,
  selectFocusedMachine,
  selectStartAndEnd,
  selectFocusedMachineLastFetched,
  selectActiveJobTypes,
  selectActiveMeasurementDetails,
  selectActiveTimeSeriesTypes,
  selectLastFetchedDate,
  selectFocusedJob,
} from "../selectors";
import { data } from "./dataSaga";
import { DataFetchedType } from "@/store/state";
import { subMilliseconds } from "date-fns";
import { RootState, rootActions } from "@/store";
import { getGlobalConfigs } from "@/index";
import { TimeSeriesValueCompactValue } from "@centralwebteam/narwhal";
import { selectLastFetchedForRefresh } from "./selector";
import { matchSampleRate } from "@/modules/sampleRate";

export function* liveRefreshWatcher({
  delayDuration,
}: {
  delayDuration: number;
}) {
  while (true) {
    // Loop every 1 second
    yield* delay(1000);

    const isRollingMode = yield* select(selectIsRollingMode);
    if (!isRollingMode) {
      continue;
    }

    const isLiveRefreshModeOn = yield* select(
      (state: RootState) => state.global.isLiveRefreshModeOn
    );
    if (!isLiveRefreshModeOn) {
      continue;
    }

    // Check if current date is after last fetched date + refresh rate
    const timeSinceLastFetchCompleted = yield* select(
      selectLastFetchedForRefresh
    );
    if (timeSinceLastFetchCompleted) {
      const now = new Date();
      const timeSinceLastFetch =
        now.getTime() - timeSinceLastFetchCompleted.getTime();

      if (timeSinceLastFetch < delayDuration) {
        continue;
      }
    }

    const { cancel } = yield* race({
      fetchDelta: call(liveRefresh),
      cancel: call(waitForLiveRefreshModeChanged),
    });

    if (cancel) {
      yield* put(rootActions.machineAnalysis.fetchingCancelled());
    }
  }
}

function* waitForLiveRefreshModeChanged() {
  do {
    const isLiveRefreshModeOn = yield* select(
      (state: RootState) => state.global.isLiveRefreshModeOn
    );
    if (!isLiveRefreshModeOn) return true;
  } while (yield* take(rootActions.global.liveRefreshModeStateChanged));
}

function* liveRefresh() {
  const config = getGlobalConfigs();
  const isLiveRefreshModeOn = yield* select(
    (state: RootState) => state.global.isLiveRefreshModeOn
  );
  if (isLiveRefreshModeOn) {
    yield* put(
      rootActions.machineAnalysis.fetchingStarted({ loadingType: "data" })
    );
    const focusedMachine = yield* select(selectFocusedMachine);
    if (!focusedMachine) return;
    const [, to] = yield* select(selectStartAndEnd);
    const lastFetched = yield* select(selectFocusedMachineLastFetched);
    const now = new Date().toISOString();
    const activeJobTypes = yield* select(selectActiveJobTypes);
    const activeTimeSeries = yield* select(selectActiveTimeSeriesTypes);
    const activeMeasurementSeries = yield* select(
      selectActiveMeasurementDetails
    );

    const options = {
      from: lastFetched ?? to,
      to: now,
      machineId: focusedMachine.id,
    };

    const fromStates = yield* select(
      selectLastFetchedDate("states" as DataFetchedType)
    );
    const fromJobs = yield* select(
      selectLastFetchedDate("jobs" as DataFetchedType)
    );
    const fromAlerts = yield* select(
      selectLastFetchedDate("alerts" as DataFetchedType)
    );
    const fromTimeSeriesValues = yield* select(
      selectLastFetchedDate("timeSeriesValues" as DataFetchedType)
    );
    const fromMeasurementValues = yield* select(
      selectLastFetchedDate("measurementSeriesValues" as DataFetchedType)
    );
    const fromProcessActions = yield* select(
      selectLastFetchedDate("processActions" as DataFetchedType)
    );
    const fromUnitHints = yield* select(
      selectLastFetchedDate("unitHints" as DataFetchedType)
    );

    const optionsTimeSeriesValues = {
      ...options,
      from: fromTimeSeriesValues,
    };
    const optionsMeasurementSeriesValues = {
      ...options,
      from: fromMeasurementValues,
    };
    const sampleRateOverrides = yield* select(
      (state: any) => state.focusedMachine.timeSeriesSampleRateOverrides
    );
    const {
      jobs,
      alerts,
      processActions,
      states,
      uniqueMeasurements,
      uniqueTimeSeries,
      timeSeriesLimits,
      measurementValues,
      timeSeriesValues,
    } = yield* all({
      jobs: call(data.fetchJobSummaries, { ...options, from: fromJobs }),
      alerts: call(data.fetchAlerts, { ...options, from: fromAlerts }),
      processActions: call(data.fetchProcessActions, {
        ...options,
        from: fromProcessActions,
      }),
      states: call(data.fetchMachineStates, { ...options, from: fromStates }),
      uniqueMeasurements: call(data.fetchUniqueMeasurementsTypes, {
        ...options,
        jobs: activeJobTypes,
      }),
      uniqueTimeSeries: call(data.fetchUniqueTimeSeries, options),
      timeSeriesLimits: all(
        activeTimeSeries.map(({ id }) =>
          call(data.fetchTimeSeriesLimits, {
            ...options,
            id,
          })
        )
      ),
      measurementValues: all(
        activeMeasurementSeries.map(({ featureName, name }) =>
          call(data.fetchMeasurementValues, {
            ...optionsMeasurementSeriesValues,
            featureName,
            name,
            jobName: activeJobTypes,
          })
        )
      ),
      timeSeriesValues: all(
        activeTimeSeries.map(({ id, displayHints, displayHintSampleRate }) =>
          call(data.fetchTimeSeriesValues, {
            ...optionsTimeSeriesValues,
            id,
            sample: matchSampleRate(
              sampleRateOverrides[id] ??
                displayHints?.sampleRate ??
                displayHintSampleRate
            ),
          })
        )
      ),
    });

    yield* all([
      putResolve(
        rootActions.machineAnalysis.measurementTypesDeltaFetched({
          types: uniqueMeasurements,
        })
      ),
      putResolve(
        rootActions.machineAnalysis.timeSeriesTypesDeltaFetched({
          types: uniqueTimeSeries,
        })
      ),
      putResolve(
        rootActions.machineAnalysis.jobsSummariesDeltaFetched({
          jobs,
          from: options.from,
          to: options.to,
        })
      ),
      putResolve(
        rootActions.machineAnalysis.statesDeltaFetched({
          states,
          from: options.from,
          to: options.to,
        })
      ),
      putResolve(
        rootActions.machineAnalysis.alertsDeltaFetched({
          alerts,
          from: options.from,
          to: options.to,
        })
      ),
      putResolve(
        rootActions.machineAnalysis.processActionsDeltaFetched({
          events: processActions,
          from: options.from,
          to: options.to,
        })
      ),
      all(
        timeSeriesLimits.map((data) =>
          putResolve(
            rootActions.machineAnalysis.timeSeriesLimitsDeltaFetched(data)
          )
        )
      ),
      all(
        timeSeriesValues.map(
          (data: {
            id: string;
            from: string;
            to: string;
            data: TimeSeriesValueCompactValue[];
          }) =>
            putResolve(
              rootActions.machineAnalysis.timeSeriesValuesDeltaFetched(data)
            )
        )
      ),
      all(
        measurementValues.map((data) =>
          putResolve(
            rootActions.machineAnalysis.measurementValuesDeltaFetched(data)
          )
        )
      ),
    ]);

    yield* put(rootActions.machineAnalysis.unitDisplayHintsRequired());

    const unitHints = yield* call(data.fetchUnitHints, {
      ...options,
      from: fromUnitHints,
    });
    yield* put(
      rootActions.machineAnalysis.unitDisplayHintsDeltaFetched({
        hints: unitHints,
        from: fromUnitHints,
        to: options.to,
      })
    );

    yield* put(rootActions.machineAnalysis.unitDisplayHintsFetchingFinished());

    const mainFocusedJob = yield* select(selectFocusedJob);
    if (mainFocusedJob && mainFocusedJob.end) {
      const reducedEndDate = yield* select(
        (state: RootState) => state.focusedJob.reducedEndDate
      );
      let date = undefined;
      if (reducedEndDate === undefined)
        date = subMilliseconds(
          new Date(mainFocusedJob.end),
          config.refreshRateFastInMs
        );
      else date = subMilliseconds(reducedEndDate, config.refreshRateFastInMs);
      yield* put(
        rootActions.machineAnalysis.setFocusedJobReducedEndDate({
          date,
        })
      );
    }

    // Update job measurement cache for focused metrology job
    if (
      mainFocusedJob &&
      (mainFocusedJob.type === "metrology" ||
        mainFocusedJob.type === "mastering")
    ) {
      yield* put(
        rootActions.machineAnalysis.jobMeasurementsRequested({
          jobName: mainFocusedJob.name,
          fetchType: "live",
        })
      );
    }

    // once other actions have resolved (note putResolve) we can say the live refresh loop is complete
    yield* put(
      rootActions.machineAnalysis.liveRefreshCompleted({
        date: now,
      })
    );
    yield* put(
      rootActions.machineAnalysis.fetchingFinished({
        to: now,
        loadingType: "data",
      })
    );
  }
}
